import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import Add from '@mui/icons-material/Add';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { toast } from 'react-toastify';
import { formatCurrency, formatPercentage } from '../../../utils/CurrencyFormat';
import {
  EffortLevel,
  MonthlyCapitalEfficiencyDashboardResponse,
  MonthlyCapitalEfficiencyMonthBlock,
  MonthlyCapitalEfficiencyUpsertRequest,
  RiskLevel,
  getMonthlyCapitalEfficiencyDashboard,
  upsertMonthlyCapitalEfficiencySnapshot,
} from '../../../services/request/Reports';

function ymNow(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function ymMonthsAgo(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

const PIE_COLORS = ['#1565c0', '#2e7d32', '#ef6c00', '#6a1b9a', '#00838f', '#5d4037'];

const riskOptions: RiskLevel[] = ['Low', 'Medium', 'High'];
const effortOptions: EffortLevel[] = ['Low', 'Medium', 'High'];

const MonthlyCapitalEfficiencyTracking: React.FC = () => {
  const [fromMonth, setFromMonth] = useState(ymMonthsAgo(11));
  const [toMonth, setToMonth] = useState(ymNow());

  const [data, setData] = useState<MonthlyCapitalEfficiencyDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dlg, setDlg] = useState({ open: false });
  const [form, setForm] = useState<MonthlyCapitalEfficiencyUpsertRequest>({
    assetId: '',
    assetType: 'Lending',
    month: ymNow(),
    startValue: 0,
    endValue: 0,
    capitalAdded: 0,
    capitalWithdrawn: 0,
    incomeGenerated: 0,
    expenses: 0,
    riskLevel: 'Medium',
    effortLevel: 'Medium',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMonthlyCapitalEfficiencyDashboard(fromMonth, toMonth);
      if (res.data) setData(res.data);
      else setData(null);
    } catch (e: unknown) {
      setData(null);
      setError(e instanceof Error ? e.message : 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [fromMonth, toMonth]);

  useEffect(() => {
    void load();
  }, [load]);

  const months = data?.months ?? [];
  const last = months.length ? months[months.length - 1] : null;

  const trendRows = useMemo(() => {
    return months.map((m) => ({
      month: m.month,
      portfolioMce: Number(m.portfolioMcePct.toFixed(2)),
      annualized: Number(m.annualizedCePct.toFixed(2)),
    }));
  }, [months]);

  const lastBarRows = useMemo(() => {
    if (!last) return [];
    return last.rows.map((r) => ({
      name: `${r.assetType} (${r.assetId})`,
      mce: Number(r.mcePct.toFixed(2)),
    }));
  }, [last]);

  const lastAllocationRows = useMemo(() => {
    if (!last) return [];
    return last.rows
      .filter((r) => r.startValue > 0)
      .map((r) => ({ name: r.assetType, value: r.startValue }))
      .reduce((acc: Array<{ name: string; value: number }>, x) => {
        const existing = acc.find((a) => a.name === x.name);
        if (existing) existing.value += x.value;
        else acc.push({ ...x });
        return acc;
      }, []);
  }, [last]);

  const openAdd = () => {
    setForm((f) => ({ ...f, month: ymNow() }));
    setDlg({ open: true });
  };

  const closeAdd = () => setDlg({ open: false });

  const save = async () => {
    try {
      if (!form.assetId.trim()) {
        toast.error('Asset ID is required.');
        return;
      }
      if (!/^[0-9]{4}-[0-9]{2}$/.test(form.month)) {
        toast.error('Month must be YYYY-MM.');
        return;
      }
      await upsertMonthlyCapitalEfficiencySnapshot({
        ...form,
        assetId: form.assetId.trim(),
        assetType: form.assetType.trim(),
      });
      toast.success('Snapshot saved.');
      closeAdd();
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save.');
    }
  };

  const monthSummaryCard = (m: MonthlyCapitalEfficiencyMonthBlock) => (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
          <Chip label={`Month: ${m.month}`} color="primary" variant="outlined" />
          <Chip label={`Start: ${formatCurrency(m.totalStartCapital, 'INR', true)}`} variant="outlined" />
          <Chip label={`End: ${formatCurrency(m.totalEndCapital, 'INR', true)}`} variant="outlined" />
          <Chip label={`Net: ${formatCurrency(m.totalNetReturn, 'INR', true)}`} color={m.totalNetReturn >= 0 ? 'success' : 'error'} variant="outlined" />
          <Chip label={`Portfolio MCE: ${formatPercentage(m.portfolioMcePct)}`} color="secondary" variant="outlined" />
          <Chip label={`Annualized: ${formatPercentage(m.annualizedCePct)}`} variant="outlined" />
          <Chip label={`Positive share: ${formatPercentage(m.positiveReturnSharePct)}`} variant="outlined" />
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5">Monthly capital efficiency (MCE)</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Add />} onClick={openAdd}>
            Add snapshot
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Net monthly return = (End − Start) + Income − Capital Added + Capital Withdrawn − Expenses. MCE% = NetReturn / Start × 100.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField label="From (YYYY-MM)" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} />
        <TextField label="To (YYYY-MM)" value={toMonth} onChange={(e) => setToMonth(e.target.value)} />
      </Stack>

      {loading && <CircularProgress size={32} sx={{ display: 'block', mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.message}
        </Alert>
      )}

      {months.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Trend (portfolio MCE)
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="portfolioMce" name="Portfolio MCE %" stroke="#1565c0" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="annualized" name="Annualized CE %" stroke="#2e7d32" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {last && (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {last.month}: asset MCE ranking
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={lastBarRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" hide />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="mce" name="MCE %" fill="#3949ab" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {last.month}: capital allocation (start value)
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={lastAllocationRows} dataKey="value" nameKey="name" outerRadius={95} label>
                      {lastAllocationRows.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => formatCurrency(Number(v), 'INR', true)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}

      {months.length === 0 && !loading && <Alert severity="warning">No snapshots in this range. Add snapshots to start tracking.</Alert>}

      {months.map((m, idx) => (
        <Accordion key={m.month} defaultExpanded={idx === months.length - 1} disableGutters sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>{monthSummaryCard(m)}</AccordionSummary>
          <AccordionDetails>
            {m.insights.length > 0 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {m.insights.map((s, i) => (
                  <Alert key={i} severity="success" variant="outlined">
                    {s}
                  </Alert>
                ))}
              </Stack>
            )}
            {m.flags.length > 0 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {m.flags.map((s, i) => (
                  <Alert key={i} severity="warning" variant="outlined">
                    {s}
                  </Alert>
                ))}
              </Stack>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell align="right">Start</TableCell>
                    <TableCell align="right">End</TableCell>
                    <TableCell align="right">Net return</TableCell>
                    <TableCell align="right">MCE %</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell>Effort</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {m.rows.map((r) => (
                    <TableRow key={`${r.month}-${r.assetId}`} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>
                            {r.assetType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.assetId}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(r.startValue, 'INR', true)}</TableCell>
                      <TableCell align="right">{formatCurrency(r.endValue, 'INR', true)}</TableCell>
                      <TableCell align="right">{formatCurrency(r.netReturn, 'INR', true)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={formatPercentage(r.mcePct)}
                          color={r.mcePct >= 0 ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{r.riskLevel || '—'}</TableCell>
                      <TableCell>{r.effortLevel || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={dlg.open} onClose={closeAdd} fullWidth maxWidth="sm">
        <DialogTitle>Add / update monthly snapshot</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Asset ID" value={form.assetId} onChange={(e) => setForm((f) => ({ ...f, assetId: e.target.value }))} />
            <TextField
              label="Asset type"
              value={form.assetType}
              onChange={(e) => setForm((f) => ({ ...f, assetType: e.target.value }))}
              helperText="Examples: Lending / Stocks / Mutual Funds / Business / Other"
            />
            <TextField label="Month (YYYY-MM)" value={form.month} onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <TextField label="Start value" type="number" value={form.startValue} onChange={(e) => setForm((f) => ({ ...f, startValue: Number(e.target.value) }))} fullWidth />
              <TextField label="End value" type="number" value={form.endValue} onChange={(e) => setForm((f) => ({ ...f, endValue: Number(e.target.value) }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Capital added" type="number" value={form.capitalAdded ?? 0} onChange={(e) => setForm((f) => ({ ...f, capitalAdded: Number(e.target.value) }))} fullWidth />
              <TextField label="Capital withdrawn" type="number" value={form.capitalWithdrawn ?? 0} onChange={(e) => setForm((f) => ({ ...f, capitalWithdrawn: Number(e.target.value) }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Income generated" type="number" value={form.incomeGenerated ?? 0} onChange={(e) => setForm((f) => ({ ...f, incomeGenerated: Number(e.target.value) }))} fullWidth />
              <TextField label="Expenses" type="number" value={form.expenses ?? 0} onChange={(e) => setForm((f) => ({ ...f, expenses: Number(e.target.value) }))} fullWidth />
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Risk</InputLabel>
                <Select
                  label="Risk"
                  value={form.riskLevel ?? 'Medium'}
                  onChange={(e) => setForm((f) => ({ ...f, riskLevel: e.target.value as RiskLevel }))}
                >
                  {riskOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Effort</InputLabel>
                <Select
                  label="Effort"
                  value={form.effortLevel ?? 'Medium'}
                  onChange={(e) => setForm((f) => ({ ...f, effortLevel: e.target.value as EffortLevel }))}
                >
                  {effortOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd}>Cancel</Button>
          <Button variant="contained" onClick={() => void save()}>
            Save snapshot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyCapitalEfficiencyTracking;

